import { useTranslation } from 'react-i18next';

import { RaViewModel } from '@work4all/models/lib/Classes/RaViewModel.entity';
import { SortDirection } from '@work4all/models/lib/DataProvider';
import { Entities } from '@work4all/models/lib/Enums/Entities.enum';
import { PaymentStatus } from '@work4all/models/lib/Enums/PaymentStatus.enum';

import { usePaymentMask } from '../../../hooks/use-payment-mask';
import { useBottomViews } from '../../bottom-views';
import { AccountingLockCell } from '../AccountingLockCell';
import { DueDateClassifiedCell } from '../DueDateClassifiedCell';
import { EntityTableV8, IEntityTableV8 } from '../entity-table/EntityTableV8';
import { useEntityDataTableV8 } from '../entity-table/use-entity-data-table-v8';
import { InvoiceFormCell } from '../InvoiceFormCell';
import { PaymentStatusCell } from '../PaymentStatusCell';
import { RaAccountingStatusCell } from '../RaAccountingStatusCell';
import { useDeleteEntitiesToolbarConfig } from '../use-delete-entities-toolbar-config';
import { useErpHandlersTemplateV8 } from '../use-erp-handlers-template-v8';

import schema from './../schemata/raViewModel-table-schema.json';

const defaultSort = [
  { field: 'invoiceDate', direction: SortDirection.DESCENDING },
];

const DELETABLE_STATUS = [
  PaymentStatus.ZERO_INVOICE,
  PaymentStatus.OPEN_CREDIT,
  PaymentStatus.OPEN,
  PaymentStatus.DUE,
];

export function RaTable(props: IEntityTableV8) {
  const dataTable = useEntityDataTableV8<RaViewModel, never>({
    ...props,
    schema: schema as never,
    defaultSort,
    enableFooter: true,
    cells: {
      PaymentStatus: PaymentStatusCell,
      DueDateClassified: DueDateClassifiedCell,
      InvoiceForm: InvoiceFormCell,
      RaAccountingStatus: RaAccountingStatusCell,
      AccountingLock: AccountingLockCell,
    },
    forceRequestFields: {
      accountingHandoverDate: null,
    } as RaViewModel,
  });

  const template = useErpHandlersTemplateV8(
    dataTable.table,
    'supplier.id',
    Entities.supplier
  );

  const { t } = useTranslation();
  const deleteConfig = useDeleteEntitiesToolbarConfig({
    entityType: Entities.ra,
    selectedEntities: dataTable.selectedEntities,
  });

  const openPaymentMaskAction = usePaymentMask(
    Entities.raViewModel,
    dataTable.refetch
  );

  const prefilter = [{ id: { $eq: dataTable.selectedEntity?.id } }];
  const bottomArea = useBottomViews({
    skip: !dataTable.selectedEntity,
    views: [
      {
        type: 'table',
        entity: Entities.raLedgerAccountSplit,
        titleTranslationKey: 'COMMON.BOOKINGS',
        options: {
          prefilter,
        },
      },
      {
        type: 'table',
        entity: Entities.raPayment,
        titleTranslationKey: 'COMMON.PAYMENTS',
        options: {
          prefilter,
        },
      },
    ],
  });

  return (
    <EntityTableV8
      {...props}
      {...dataTable}
      displayFooter
      template={template}
      onRowDoubleClick={() => true}
      actions={{
        add: undefined,
        edit: undefined,
        custom: { left: [openPaymentMaskAction] },
        remove: {
          ...deleteConfig,
          handler: async (e) => {
            await deleteConfig.handler(e);
            dataTable.refetch();
          },
          canDeleteEntity: (id) => {
            const entity = dataTable.selectedEntities.find(
              (item) => item.id === Number(id)
            );
            const canDelete =
              DELETABLE_STATUS.includes(entity.paymentStatus) &&
              !entity.accountingHandoverDate
                ? true
                : false;

            const preventMessage = !DELETABLE_STATUS.includes(
              entity.paymentStatus
            )
              ? t('ALERTS.OUTGOING_INVOICE_PAYMENTS_EXIST', {
                  number: entity?.invoiceNumber,
                })
              : entity.accountingHandoverDate
              ? t('ALERTS.OUTGOING_INVOICE_HANDOVER', {
                  number: entity?.invoiceNumber,
                })
              : null;

            return {
              value: canDelete,
              preventMessage,
            };
          },
        },
      }}
      areas={{
        bottom: bottomArea,
      }}
    />
  );
}
